import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  adminListPaintings,
  adminCreatePainting,
  adminUpdatePainting,
  adminDeletePainting,
  adminDeleteAllPaintings,
  adminReorderPaintings,
  adminUpload,
  fetchMe,
  logout,
  resolveImage,
  formatApiErrorDetail,
  auth,
} from "@/lib/adminApi";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import SubscribersPanel from "@/components/SubscribersPanel";
import InquiriesPanel from "@/components/InquiriesPanel";
import BlogPanel from "@/components/BlogPanel";
import TestimonialsPanel from "@/components/TestimonialsPanel";

const TAB_TITLES = { paintings: "Paintings", blog: "Blog", testimonials: "Testimonials", subscribers: "Subscribers", inquiries: "Inquiries" };

const EMPTY = {
  title: "",
  medium: "",
  dimensions: "",
  price: "",
  story: "",
  image_url: "",
  alt: "",
  room_photos: [],
  status_tag: "",
};

const STICKERS = ["", "Sold", "DISCOUNTED", "NEW", "RESERVED", "AVAILABLE"];
const isSold = (t) => (t || "").toLowerCase() === "sold" || (t || "").toLowerCase() === "sold out";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // painting object or {} for create
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState("paintings");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const guard = async () => {
    if (!auth.token) { navigate("/admin/login", { replace: true }); return false; }
    try {
      const user = await fetchMe();
      setMe(user);
      return true;
    } catch {
      auth.clear();
      navigate("/admin/login", { replace: true });
      return false;
    }
  };

  const load = async () => {
    try {
      const data = await adminListPaintings();
      setItems(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    (async () => {
      const ok = await guard();
      if (ok) await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminDeletePainting(p.id);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    await load();
  };

  const onDeleteAll = async () => {
    if (!window.confirm(`Delete ALL ${items.length} paintings? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminDeleteAllPaintings();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
    await load();
  };

  const openCreate = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({
    ...EMPTY, ...p,
    status_tag: isSold(p.status_tag) ? "Sold" : (p.status_tag || ""),
    room_photos: p.room_photos || [],
  });

  const onDrop = async (targetId) => {
    setOverId(null);
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const from = items.findIndex((p) => p.id === dragId);
    const to = items.findIndex((p) => p.id === targetId);
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const renumbered = next.map((p, i) => ({ ...p, order: i + 1 }));
    setItems(renumbered);
    setDragId(null);
    try {
      await adminReorderPaintings(renumbered.map((p) => p.id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await load();
    }
  };

  const uploadMain = async (file) => {
    setBusy(true);
    try {
      const { url } = await adminUpload(file);
      setEditing((s) => ({ ...s, image_url: url }));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const [uploadProgress, setUploadProgress] = useState("");

  const uploadRooms = async (files) => {
    const list = Array.from(files || []);
    const room = (editing.room_photos || []).length;
    const allowed = list.slice(0, Math.max(0, 5 - room));
    if (!allowed.length) return;
    if (allowed.length < list.length) setError(`Only ${allowed.length} more photo(s) allowed (max 5).`);
    setBusy(true);
    try {
      for (let i = 0; i < allowed.length; i++) {
        setUploadProgress(`Uploading ${i + 1} of ${allowed.length}…`);
        const { url } = await adminUpload(allowed[i]);
        setEditing((s) => ({ ...s, room_photos: [...(s.room_photos || []), url].slice(0, 5) }));
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); setUploadProgress(""); }
  };

  const removeRoom = (i) => {
    setEditing((s) => ({
      ...s,
      room_photos: s.room_photos.filter((_, k) => k !== i),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = {
        title: editing.title,
        medium: editing.medium,
        dimensions: editing.dimensions,
        price: editing.price || "",
        story: editing.story,
        image_url: editing.image_url,
        alt: editing.alt || editing.title,
        room_photos: editing.room_photos || [],
        status_tag: editing.status_tag || null,
      };
      if (editing.id) {
        await adminUpdatePainting(editing.id, body);
      } else {
        await adminCreatePainting(body);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-root" data-testid="admin-dashboard">
      <header className="admin-header">
        <div>
          <p className="mono-caps admin-over">Warren Wulkan · Admin</p>
          <h1 className="admin-title">{TAB_TITLES[tab]}</h1>
        </div>
        <div className="admin-header__actions">
          <Link to="/" className="admin-link mono-caps">View gallery &rarr;</Link>
          {tab === "paintings" && <button className="admin-btn mono-caps" onClick={openCreate} data-testid="admin-new">+ New painting</button>}
          <button className="admin-btn admin-btn--ghost mono-caps" onClick={() => setShowPassword(true)} data-testid="admin-change-password">Change password</button>
          <button className="admin-btn admin-btn--ghost mono-caps" onClick={onLogout} data-testid="admin-logout">
            Sign out {me?.email ? `(${me.email})` : ""}
          </button>
        </div>
      </header>

      {error && <p className="admin-error" data-testid="admin-error">{error}</p>}

      <div className="admin-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "paintings"} className={`admin-tab mono-caps${tab === "paintings" ? " is-active" : ""}`} onClick={() => setTab("paintings")} data-testid="admin-tab-paintings">Paintings</button>
        <button type="button" role="tab" aria-selected={tab === "blog"} className={`admin-tab mono-caps${tab === "blog" ? " is-active" : ""}`} onClick={() => setTab("blog")} data-testid="admin-tab-blog">Blog</button>
        <button type="button" role="tab" aria-selected={tab === "testimonials"} className={`admin-tab mono-caps${tab === "testimonials" ? " is-active" : ""}`} onClick={() => setTab("testimonials")} data-testid="admin-tab-testimonials">Testimonials</button>
        <button type="button" role="tab" aria-selected={tab === "subscribers"} className={`admin-tab mono-caps${tab === "subscribers" ? " is-active" : ""}`} onClick={() => setTab("subscribers")} data-testid="admin-tab-subscribers">Subscribers</button>
        <button type="button" role="tab" aria-selected={tab === "inquiries"} className={`admin-tab mono-caps${tab === "inquiries" ? " is-active" : ""}`} onClick={() => setTab("inquiries")} data-testid="admin-tab-inquiries">Inquiries</button>
      </div>

      {tab === "subscribers" && <SubscribersPanel />}
      {tab === "inquiries" && <InquiriesPanel />}
      {tab === "blog" && <BlogPanel authorDefault="Warren Wulkan" />}
      {tab === "testimonials" && <TestimonialsPanel />}

      {tab === "paintings" && <>
      <p className="mono-caps admin-hint" data-testid="admin-reorder-hint">Drag a card to change the walkthrough order — saves on drop.</p>
      {items.length === 0 && <p className="admin-subs__empty" data-testid="admin-empty">No paintings yet — add your first one with “+ New painting”.</p>}
      {items.length > 1 && (
        <p className="admin-danger-row">
          <button type="button" className="admin-mini admin-mini--danger mono-caps" onClick={onDeleteAll} disabled={busy} data-testid="admin-delete-all">Delete all paintings</button>
        </p>
      )}

      <div className="admin-grid">
        {items.map((p) => (
          <article
            key={p.id}
            className={`admin-card${dragId === p.id ? " is-dragging" : ""}${overId === p.id && dragId !== p.id ? " is-over" : ""}`}
            data-testid={`admin-card-${p.order}`}
            draggable
            onDragStart={(e) => { setDragId(p.id); e.dataTransfer.effectAllowed = "move"; }}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onDragOver={(e) => { e.preventDefault(); if (overId !== p.id) setOverId(p.id); }}
            onDragLeave={() => overId === p.id && setOverId(null)}
            onDrop={(e) => { e.preventDefault(); onDrop(p.id); }}
          >
            <div className="admin-card__thumb">
              <span className="admin-card__grip mono-caps" aria-hidden="true">⋮⋮</span>
              <img src={resolveImage(p.image_url)} alt={p.alt} loading="lazy" draggable={false} />
              {p.status_tag && <span className="admin-card__sticker">{p.status_tag}</span>}
            </div>
            <div className="admin-card__body">
              <p className="mono-caps admin-card__num">Order {String(p.order).padStart(2, "0")}</p>
              <h3 className="admin-card__title">{p.title}</h3>
              <p className="mono-caps admin-card__spec">{p.medium} · {p.dimensions_display || p.dimensions}{p.price_display ? ` · ${p.price_display}` : ""}</p>
              <p className="mono-caps admin-card__slug">/paintings/{p.slug}</p>
              <div className="admin-card__actions">
                <button type="button" onClick={() => openEdit(p)} className="admin-mini mono-caps" data-testid={`admin-edit-${p.order}`}>Edit</button>
                <button type="button" onClick={() => onDelete(p)} className="admin-mini admin-mini--danger mono-caps" data-testid={`admin-delete-${p.order}`}>Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>
      </>}

      {editing && (
        <div className="admin-modal" role="dialog" aria-modal="true" data-testid="admin-modal">
          <button className="admin-modal__scrim" onClick={() => setEditing(null)} aria-label="Close" />
          <form className="admin-modal__panel" onSubmit={save}>
            <div className="admin-modal__top">
              <h2>{editing.id ? "Edit painting" : "New painting"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="admin-mini mono-caps" data-testid="admin-modal-close">Close</button>
            </div>

            <div className="admin-modal__body">
              <div className="admin-modal__images">
                <label className="admin-field">
                  <span className="mono-caps">Main image</span>
                  {editing.image_url ? (
                    <div className="admin-preview">
                      <img src={resolveImage(editing.image_url)} alt="" />
                      <button type="button" className="admin-mini" onClick={() => setEditing((s) => ({...s, image_url: ""}))}>Remove</button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadMain(e.target.files[0])}
                      data-testid="admin-upload-main"
                    />
                  )}
                </label>

                <label className="admin-field">
                  <span className="mono-caps">Room photos (up to 5)</span>
                  <div className="admin-rooms">
                    {(editing.room_photos || []).map((u, i) => (
                      <div key={u} className="admin-room">
                        <img src={resolveImage(u)} alt="" />
                        <button type="button" onClick={() => removeRoom(i)} className="admin-mini mono-caps">Remove</button>
                      </div>
                    ))}
                    {(editing.room_photos || []).length < 5 && (
                      <label className="admin-drop" data-testid="admin-upload-room-drop">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => { uploadRooms(e.target.files); e.target.value = ""; }}
                          data-testid="admin-upload-room"
                        />
                        <span className="mono-caps">
                          {uploadProgress || `Add up to ${5 - (editing.room_photos || []).length} photo(s) at once`}
                        </span>
                      </label>
                    )}
                  </div>
                </label>
              </div>

              <div className="admin-modal__fields">
                <label className="admin-field"><span className="mono-caps">Title</span>
                  <input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} required data-testid="admin-field-title" />
                </label>
                <label className="admin-field"><span className="mono-caps">Medium</span>
                  <input value={editing.medium} onChange={(e) => setEditing({...editing, medium: e.target.value})} placeholder="e.g. Acrylic on canvas" required />
                </label>
                <div className="admin-row">
                  <label className="admin-field"><span className="mono-caps">Dimensions</span>
                    <input value={editing.dimensions} onChange={(e) => setEditing({...editing, dimensions: e.target.value})} placeholder="e.g. 24 × 36" required data-testid="admin-field-dimensions" />
                  </label>
                  <label className="admin-field"><span className="mono-caps">Price{isSold(editing.status_tag) ? " (optional)" : ""}</span>
                    <input value={editing.price} onChange={(e) => setEditing({...editing, price: e.target.value})} placeholder="e.g. $350.00" required={!isSold(editing.status_tag)} data-testid="admin-field-price" />
                  </label>
                </div>
                <label className="admin-field"><span className="mono-caps">Sticker</span>
                  <select value={editing.status_tag || ""} onChange={(e) => setEditing({...editing, status_tag: e.target.value})} data-testid="admin-field-sticker">
                    {STICKERS.map((s) => <option key={s} value={s}>{s || "— none —"}</option>)}
                  </select>
                </label>
                <label className="admin-field"><span className="mono-caps">Story</span>
                  <textarea rows={5} value={editing.story} onChange={(e) => setEditing({...editing, story: e.target.value})} required />
                </label>
                <label className="admin-field"><span className="mono-caps">Alt text</span>
                  <input value={editing.alt} onChange={(e) => setEditing({...editing, alt: e.target.value})} placeholder="Describe the image for accessibility" />
                </label>
              </div>
            </div>

            <div className="admin-modal__actions">
              <button type="submit" className="admin-btn mono-caps" disabled={busy || !editing.image_url} data-testid="admin-save">
                {busy ? "Saving…" : editing.id ? "Save changes" : "Create painting"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
    </div>
  );
}
